import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';

const STATUS_OPTIONS = [
  { value: 'identified', label: 'Identified' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'committed', label: 'Committed' },
  { value: 'declined', label: 'Declined' },
  { value: 'no_response', label: 'No response' },
];

const CONTRIBUTION_TYPES = [
  { value: 'sponsor', label: 'Sponsor' },
  { value: 'raffle_prize', label: 'Raffle prize' },
  { value: 'donation', label: 'Cash donation' },
  { value: 'in_kind', label: 'In-kind' },
  { value: 'volunteer', label: 'Volunteer' },
];

export default function BusinessDetailPanel({ businessId, campaignId, onClose }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['business', businessId, campaignId],
    queryFn: () => api.getBusiness(businessId, campaignId),
  });

  const business = data?.business;
  const prospect = business?.prospect;

  const [formOpen, setFormOpen] = useState(false);

  async function handleSaveProspect(formData) {
    if (prospect) {
      await api.updateProspect(prospect.id, formData);
    } else {
      await api.createProspect({
        campaign_id: campaignId,
        business_id: businessId,
        ...formData,
      });
    }
    setFormOpen(false);
    queryClient.invalidateQueries({ queryKey: ['business', businessId] });
    queryClient.invalidateQueries({ queryKey: ['businesses'] });
    queryClient.invalidateQueries({ queryKey: ['campaignStats'] });
  }

  async function handleLogInteraction(data) {
    if (!prospect) return;
    await api.logInteraction(prospect.id, data);
    queryClient.invalidateQueries({ queryKey: ['business', businessId] });
  }

  if (isLoading || !business) {
    return (
      <aside className="w-96 bg-white border-l border-gray-200 overflow-y-auto p-4">
        Loading...
      </aside>
    );
  }

  return (
    <aside className="w-96 bg-white border-l border-gray-200 overflow-y-auto">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900">{business.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          <div>{business.address}</div>
          <div>
            {business.city}, {business.state} {business.zip}
          </div>
          {business.phone && <div className="mt-1">{business.phone}</div>}
          {business.website && (
            <div>
              <a
                href={`https://${business.website.toLowerCase()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ccf-primary hover:underline"
              >
                {business.website}
              </a>
            </div>
          )}
        </div>

        {/* Key stats */}
        <div className="grid grid-cols-2 gap-2 text-xs mb-4">
          <StatBox label="Category" value={business.primary_sic_description} />
          <StatBox
            label="Revenue"
            value={business.annual_revenue ? `$${(business.annual_revenue / 1000).toFixed(0)}K` : '—'}
          />
          <StatBox label="Employees" value={business.employee_range || '—'} />
          <StatBox label="Credit" value={business.credit_rating || '—'} />
        </div>

        {/* Primary contact */}
        {business.contact_name && (
          <div className="bg-gray-50 rounded p-3 mb-4 text-sm">
            <div className="text-xs font-semibold text-gray-600 uppercase mb-1">
              Primary Contact
            </div>
            <div className="font-medium">{business.contact_name}</div>
            {business.contact_title && (
              <div className="text-gray-600 text-xs">{business.contact_title}</div>
            )}
          </div>
        )}

        {/* Additional executives */}
        {business.executives && business.executives.length > 1 && (
          <div className="mb-4 text-sm">
            <div className="text-xs font-semibold text-gray-600 uppercase mb-1">
              Additional Contacts ({business.executives.length})
            </div>
            <ul className="text-xs space-y-0.5">
              {business.executives.slice(0, 5).map((e, i) => (
                <li key={i} className="text-gray-700">
                  {e.first} {e.last}
                  {e.title && <span className="text-gray-500"> — {e.title}</span>}
                </li>
              ))}
              {business.executives.length > 5 && (
                <li className="text-gray-500">+{business.executives.length - 5} more</li>
              )}
            </ul>
          </div>
        )}

        {/* Prospect section */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">Campaign Prospect</h3>
            {prospect && (
              <StatusBadge status={prospect.status} />
            )}
          </div>

          {prospect && !formOpen && (
            <div className="text-sm text-gray-700 mb-2">
              {prospect.contribution_type && (
                <div>Type: {prospect.contribution_type.replace('_', ' ')}</div>
              )}
              {prospect.ask_amount && (
                <div>Ask: ${prospect.ask_amount.toLocaleString()}</div>
              )}
              {prospect.committed_amount && (
                <div className="text-ccf-accent font-semibold">
                  Committed: ${prospect.committed_amount.toLocaleString()}
                </div>
              )}
              {prospect.assigned_to && (
                <div className="text-xs text-gray-500">Assigned to {prospect.assigned_to}</div>
              )}
              {prospect.notes && (
                <div className="text-xs text-gray-600 mt-2 italic">"{prospect.notes}"</div>
              )}
            </div>
          )}

          {!formOpen && (
            <button
              onClick={() => setFormOpen(true)}
              className="w-full text-sm bg-ccf-primary text-white rounded px-3 py-1.5 hover:bg-blue-900"
            >
              {prospect ? 'Update Prospect' : 'Mark as Prospect'}
            </button>
          )}

          {formOpen && (
            <ProspectForm
              prospect={prospect}
              onSave={handleSaveProspect}
              onCancel={() => setFormOpen(false)}
            />
          )}
        </div>

        {/* Interactions log */}
        {prospect?.interactions && prospect.interactions.length > 0 && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="font-semibold text-gray-900 mb-2">Activity</h3>
            <ul className="space-y-2 text-xs">
              {prospect.interactions.map((i) => (
                <li key={i.id} className="border-l-2 border-gray-200 pl-2">
                  <div className="text-gray-500">
                    {i.board_member} · {i.interaction_type} · {new Date(i.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-gray-800">{i.summary}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Quick log interaction */}
        {prospect && (
          <QuickInteractionLogger onLog={handleLogInteraction} />
        )}
      </div>
    </aside>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-gray-50 rounded p-2">
      <div className="text-gray-500 uppercase text-[10px]">{label}</div>
      <div className="text-gray-900 truncate">{value || '—'}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    identified: 'bg-gray-100 text-gray-700',
    contacted: 'bg-blue-100 text-blue-700',
    interested: 'bg-purple-100 text-purple-700',
    committed: 'bg-emerald-100 text-emerald-700',
    declined: 'bg-red-100 text-red-700',
    no_response: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${colors[status] || 'bg-gray-100'}`}>
      {status}
    </span>
  );
}

function ProspectForm({ prospect, onSave, onCancel }) {
  const [form, setForm] = useState({
    status: prospect?.status || 'contacted',
    contribution_type: prospect?.contribution_type || '',
    ask_amount: prospect?.ask_amount || '',
    committed_amount: prospect?.committed_amount || '',
    notes: prospect?.notes || '',
  });

  async function handleSubmit(e) {
    e.preventDefault();
    await onSave({
      ...form,
      ask_amount: form.ask_amount ? parseFloat(form.ask_amount) : null,
      committed_amount: form.committed_amount ? parseFloat(form.committed_amount) : null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2 text-sm">
      <select
        value={form.status}
        onChange={(e) => setForm({ ...form, status: e.target.value })}
        className="w-full border border-gray-300 rounded px-2 py-1"
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </select>

      <select
        value={form.contribution_type}
        onChange={(e) => setForm({ ...form, contribution_type: e.target.value })}
        className="w-full border border-gray-300 rounded px-2 py-1"
      >
        <option value="">Contribution type...</option>
        {CONTRIBUTION_TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>

      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          placeholder="Ask $"
          value={form.ask_amount}
          onChange={(e) => setForm({ ...form, ask_amount: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1"
        />
        <input
          type="number"
          placeholder="Committed $"
          value={form.committed_amount}
          onChange={(e) => setForm({ ...form, committed_amount: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1"
        />
      </div>

      <textarea
        placeholder="Notes..."
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        rows={2}
        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
      />

      <div className="flex gap-2">
        <button
          type="submit"
          className="flex-1 bg-ccf-primary text-white rounded px-3 py-1 hover:bg-blue-900"
        >
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-gray-800 rounded px-3 py-1 hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function QuickInteractionLogger({ onLog }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ interaction_type: 'note', summary: '' });

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.summary.trim()) return;
    await onLog(form);
    setForm({ interaction_type: 'note', summary: '' });
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-4 w-full text-xs text-ccf-primary hover:underline"
      >
        + Log activity
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-2 text-sm">
      <select
        value={form.interaction_type}
        onChange={(e) => setForm({ ...form, interaction_type: e.target.value })}
        className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
      >
        <option value="note">Note</option>
        <option value="call">Phone call</option>
        <option value="email">Email</option>
        <option value="meeting">Meeting</option>
        <option value="visit">Visit</option>
        <option value="text">Text</option>
      </select>
      <textarea
        placeholder="What happened?"
        value={form.summary}
        onChange={(e) => setForm({ ...form, summary: e.target.value })}
        rows={2}
        className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
        required
      />
      <div className="flex gap-2">
        <button
          type="submit"
          className="flex-1 bg-ccf-accent text-white rounded px-3 py-1 text-xs hover:bg-emerald-700"
        >
          Log
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex-1 bg-gray-200 text-gray-800 rounded px-3 py-1 text-xs hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
